import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { PERIOD_STATUS_LABELS, voucherSign } from "@devengo/shared";

import { Dialog, PeriodPicker } from "../components/period-picker";
import { VoidDialog } from "../components/void-dialog";
import { VoidFilter, type VoidStatus } from "../components/void-filter";
import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  PageTitle,
  numClass,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import {
  decimalCents,
  formatCents,
  toPesosCents,
  formatDate,
  periodName,
  voucherNumber,
} from "../lib/format";
import { usePeriods, type VoucherView } from "../lib/hooks";
import { parsePeriod, periodParam } from "../lib/session";

export interface VouchersSearch {
  periodo?: string;
  dir?: "compra" | "venta";
  /** Vigentes when absent (plan 024). */
  estado?: "anulados" | "todos";
}

const sign = (v: VoucherView) => voucherSign(v.typeCode);

/**
 * Amounts per column. With `rate`, each line is converted to pesos on its own before adding,
 * exactly as the books and the Caja IVA do, so the totals agree to the cent (Codex final pass).
 */
function amounts(v: VoucherView, rate?: string) {
  const conv = (cents: number) => (rate ? toPesosCents(cents, rate) : cents);
  let gravado = 0;
  let iva = 0;
  let otros = 0;
  let exento = 0;
  for (const line of v.lines) {
    const net = conv(decimalCents(line.net));
    const tax = conv(decimalCents(line.tax));
    if (line.kind === "gravado") {
      gravado += net;
      iva += tax;
    } else if (line.kind === "exento" || line.kind === "no_gravado") exento += net;
    else otros += tax;
  }
  return { gravado, iva, otros, exento, total: conv(decimalCents(v.total)) };
}

export function VouchersPage({ companyId, search }: { companyId: string; search: VouchersSearch }) {
  const navigate = useNavigate();
  const { year, month } = parsePeriod(search.periodo);
  const periodo = periodParam(year, month);
  const dir = search.dir ?? "compra";
  const estado = search.estado ?? "vigentes";
  const periods = usePeriods(companyId);
  const period = periods.data?.find((p) => p.year === year && p.month === month);
  const vouchers = useQuery({
    queryKey: ["vouchers", companyId, periodo, dir, estado],
    queryFn: () =>
      api.get<VoucherView[]>(
        `/api/companies/${companyId}/periods/${year}/${month}/vouchers?direction=${dir}&status=${estado}`,
      ),
  });
  const [moving, setMoving] = useState<VoucherView | null>(null);
  const [voiding, setVoiding] = useState<VoucherView | null>(null);

  /** `status` changes the filter; absent, it keeps the current one. */
  const setSearch = (patch: Partial<VouchersSearch>, status: VoidStatus = estado) =>
    void navigate({
      to: "/empresas/$companyId/comprobantes",
      params: { companyId },
      search: { periodo, dir, ...(status !== "vigentes" ? { estado: status } : {}), ...patch },
    });

  const all = vouchers.data ?? [];
  // Totals add only what counts: voided vouchers are listed, not added (plan 024).
  const rows = all.filter((v) => !v.voided);
  // Totals in pesos, converted per voucher exactly like the Caja IVA and the books: adding
  // dollars to pesos would show a number no document agrees with (Codex hito 3-5 finding 9).
  const totals = rows.reduce(
    (acc, v) => {
      const a = amounts(v, v.exchangeRate);
      const s = sign(v);
      return {
        gravado: acc.gravado + s * a.gravado,
        iva: acc.iva + s * a.iva,
        otros: acc.otros + s * a.otros,
        exento: acc.exento + s * a.exento,
        total: acc.total + s * a.total,
      };
    },
    { gravado: 0, iva: 0, otros: 0, exento: 0, total: 0 },
  );
  const foreign = rows.some((v) => v.currency !== "ARS");

  return (
    <>
      <PageTitle
        subtitle={
          <>
            {periodName(year, month)} ·{" "}
            {period ? (
              <Badge tone={period.status === "abierto" ? "success" : "warning"}>
                {PERIOD_STATUS_LABELS[period.status]}
              </Badge>
            ) : (
              <Badge>Sin movimientos</Badge>
            )}
          </>
        }
        actions={
          <>
            <PeriodPicker value={periodo} onChange={(value) => setSearch({ periodo: value })} />
            <Link
              to="/empresas/$companyId/comprobantes/nuevo"
              params={{ companyId }}
              search={{ dir, periodo }}
              className="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Cargar comprobante
            </Link>
          </>
        }
      >
        Comprobantes
      </PageTitle>

      <div className="mb-3 flex gap-1">
        {(["compra", "venta"] as const).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setSearch({ dir: d })}
            className={`rounded-t-md border-b-2 px-4 py-1.5 text-sm font-medium ${
              dir === d
                ? "border-brand-primary text-brand-primary"
                : "border-transparent text-brand-muted hover:text-brand-text"
            }`}
          >
            {d === "compra" ? "Compras" : "Ventas"}
          </button>
        ))}
        <div className="ml-auto">
          <VoidFilter value={estado} onChange={(value) => setSearch({}, value)} />
        </div>
      </div>

      <Card>
        <ErrorBox error={vouchers.error} />
        {all.length === 0 && !vouchers.isLoading ? (
          <p className="text-sm text-brand-muted">
            {estado === "anulados"
              ? `No hay ${dir === "compra" ? "compras" : "ventas"} anuladas en ${periodName(year, month)}.`
              : `No hay ${dir === "compra" ? "compras" : "ventas"} en ${periodName(year, month)}. Cargalas a mano o importá el archivo de ARCA.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={thClass}>Fecha</th>
                  <th className={thClass}>Comprobante</th>
                  <th className={thClass}>{dir === "compra" ? "Proveedor" : "Cliente"}</th>
                  <th className={`${thClass} text-right`}>Neto gravado</th>
                  <th className={`${thClass} text-right`}>IVA</th>
                  <th className={`${thClass} text-right`}>Exento / No grav.</th>
                  <th className={`${thClass} text-right`}>Otros</th>
                  <th className={`${thClass} text-right`}>Total</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {all.map((v) => {
                  const a = amounts(v);
                  const s = sign(v);
                  return (
                    <tr
                      key={v.id}
                      className={`hover:bg-brand-tint ${v.voided ? "text-brand-muted line-through" : ""}`}
                    >
                      <td className={`${tdClass} tabular`}>{formatDate(v.issueDate)}</td>
                      <td className={tdClass}>
                        <div className="font-medium">{v.typeName}</div>
                        <div className="tabular text-xs text-brand-muted">
                          {voucherNumber(v.pointOfSale, v.number)}
                          {v.source === "import" ? " · importado" : ""}
                          {v.source === "settlement" ? " · de una liquidación" : ""}
                          {v.lines.filter((l) => l.kind === "gravado").length > 1
                            ? " · alícuotas múltiples"
                            : ""}
                        </div>
                        {v.voided ? (
                          <div className="text-xs text-red-700 no-underline [text-decoration:none]">
                            Anulado el {formatDate(v.voided.at.slice(0, 10))}: {v.voided.reason}
                          </div>
                        ) : null}
                      </td>
                      <td className={tdClass}>
                        <div>{v.counterpart.name}</div>
                        <div className="tabular text-xs text-brand-muted">
                          {v.counterpart.docDisplay}
                        </div>
                      </td>
                      <td className={`${tdClass} ${numClass}`}>{formatCents(s * a.gravado)}</td>
                      <td className={`${tdClass} ${numClass}`}>{formatCents(s * a.iva)}</td>
                      <td className={`${tdClass} ${numClass}`}>{formatCents(s * a.exento)}</td>
                      <td className={`${tdClass} ${numClass}`}>{formatCents(s * a.otros)}</td>
                      <td className={`${tdClass} ${numClass} font-medium`}>
                        {v.currency !== "ARS" ? `${v.currency} ` : ""}
                        {formatCents(s * a.total)}
                      </td>
                      <td className={`${tdClass} whitespace-nowrap text-right no-underline`}>
                        {v.settlementId ? (
                          // Only its settlement edits, moves or voids it.
                          <Link
                            to="/empresas/$companyId/liquidaciones/nueva"
                            params={{ companyId }}
                            search={{ id: v.settlementId, periodo }}
                            className="px-1 text-xs text-brand-primary hover:underline"
                          >
                            Ver liquidación
                          </Link>
                        ) : v.voided ? (
                          <button
                            type="button"
                            className="px-1 text-xs text-brand-primary hover:underline"
                            onClick={() => setVoiding(v)}
                          >
                            Reactivar
                          </button>
                        ) : (
                          <>
                            <Link
                              to="/empresas/$companyId/comprobantes/nuevo"
                              params={{ companyId }}
                              search={{ id: v.id, dir, periodo }}
                              className="px-1 text-xs text-brand-primary hover:underline"
                            >
                              Editar
                            </Link>
                            <button
                              type="button"
                              className="px-1 text-xs text-brand-primary hover:underline"
                              onClick={() => setMoving(v)}
                            >
                              Mover
                            </button>
                            <button
                              type="button"
                              className="px-1 text-xs text-red-700 hover:underline"
                              onClick={() => setVoiding(v)}
                            >
                              Anular
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className={tdClass} colSpan={3}>
                    {rows.length} comprobante{rows.length === 1 ? "" : "s"}
                    {all.length > rows.length
                      ? ` · ${all.length - rows.length} anulado${all.length - rows.length === 1 ? "" : "s"}, fuera de los totales`
                      : ""}
                    {foreign ? " · totales en pesos al tipo de cambio de cada uno" : ""}
                  </td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(totals.gravado)}</td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(totals.iva)}</td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(totals.exento)}</td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(totals.otros)}</td>
                  <td className={`${tdClass} ${numClass}`}>{formatCents(totals.total)}</td>
                  <td className={tdClass} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
      {moving ? <MoveDialog voucher={moving} onClose={() => setMoving(null)} /> : null}
      {voiding ? (
        <VoidDialog
          companyId={companyId}
          vouchers={[voiding]}
          action={voiding.voided ? "unvoid" : "void"}
          onClose={() => setVoiding(null)}
          onDone={() => setVoiding(null)}
        />
      ) : null}
    </>
  );
}

function useInvalidateFiscal(companyId: string) {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ["vouchers", companyId] });
    await client.invalidateQueries({ queryKey: ["periods", companyId] });
    await client.invalidateQueries({ queryKey: ["caja", companyId] });
  };
}

function MoveDialog({ voucher, onClose }: { voucher: VoucherView; onClose: () => void }) {
  const invalidate = useInvalidateFiscal(voucher.companyId);
  const [target, setTarget] = useState(voucher.issueDate.slice(0, 7));
  const [reason, setReason] = useState("");
  const move = useMutation({
    mutationFn: () => {
      const { year, month } = parsePeriod(target);
      return api.post(`/api/vouchers/${voucher.id}/move`, { year, month, reason });
    },
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });
  return (
    <Dialog
      title={`Mover ${voucher.typeName} ${voucherNumber(voucher.pointOfSale, voucher.number)}`}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-brand-muted">
          Sirve para diferir el comprobante a otro período. Queda registrado en la auditoría.
        </p>
        <Field label="Período destino">
          <Input type="month" value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
        <Field label="Motivo (opcional)">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: diferimos el crédito fiscal"
          />
        </Field>
        <ErrorBox error={move.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={move.isPending} onClick={() => move.mutate()}>
            Mover
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
